import { CCol, CRow } from "@coreui/react";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { ReactSortable } from "react-sortablejs";
import { apiConfig } from "../../config/apiConfig";
import { useCookie } from "../../hooks/useCookie";
import { useToast } from "../../hooks/useToast";
import apiService from "../../service/apiService";
import { offersDisplayType } from "./OfferTable";
import AdminRoute from "../auth/RestrictedRoute";
import SearchForm from "../../components/inputs/SearchForm";
import { useDebounce } from "../../hooks/useDebounce";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";

const offerPlaceholderImage = "/venez.jpeg";

interface MutationProps {
  specialsOrder: {
    spacialId: string;
    order: number;
  }[];
}

export default function OrganizeOffers() {
  const { token } = useCookie("vToken");
  const queryClient = useQueryClient();
  const { toastError, toastSuccess } = useToast();

  const [searchQuery, setSearchQuery] = useState<string>("");
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [orderInputValue, setOrderInputValue] = useState<number>(0);

  const { data, isLoading } = useQuery(
    ["getSpecials", debouncedSearch],
    async () => {
      const endpoint = apiConfig.offers.organize({
        page: 1,
        limit: "100",
        query: debouncedSearch,
      });
      return apiService.MakeGetRequest(endpoint, token);
    },
    {
      refetchOnWindowFocus: false,
      select: (data) => data.items || [],
    },
  );

  const [arrangedOffers, setArrangedOffers] = useState<SpecialOffers[]>([]);

  // Sync fetched data with local state
  useEffect(() => {
    if (data) {
      setArrangedOffers(data);
    }
  }, [data]);

  // Save order mutation
  const saveOrderMutation = useMutation(
    (data: MutationProps) =>
      apiService.MakePutRequest(apiConfig.offers.set_order(), data, token),
    {
      onError: () => toastError("Quelque chose n'a pas fonctionné"),
      onSuccess: () => {
        toastSuccess("Mise à jour de la liste des offres");
        queryClient.invalidateQueries(["getSpecials"]);
      },
    },
  );

  const handleOrderChange = (offerId: string, newOrder: number) => {
    if (newOrder < 1 || newOrder > arrangedOffers.length) return;

    const updatedOffers = [...arrangedOffers];
    const offerIndex = updatedOffers.findIndex((offer) => offer.id === offerId);
    if (offerIndex === -1) return;

    const offer = updatedOffers[offerIndex];
    updatedOffers.splice(offerIndex, 1);
    updatedOffers.splice(newOrder - 1, 0, offer);

    setArrangedOffers(
      updatedOffers.map((offer, index) => ({ ...offer, order: index + 1 })),
    );
    setEditingOrderId(null);
  };

  const savePositions = () => {
    const specialsOrder = arrangedOffers.map((special, index) => ({
      spacialId: special.id,
      order: index + 1,
    }));

    saveOrderMutation.mutate({ specialsOrder });
  };

  const isOfferUnavailable = (offer: SpecialOffers) => {
    if (offer?.isVisible !== true) return true;
    if (offer?.numberOfPersons !== null && offer?.numberOfPersons <= 0)
      return true;
    if (offer?.date && offer?.duration) {
      const endDate = new Date(offer.date);
      endDate.setDate(endDate.getDate() + Number(offer.duration));
      return new Date() > endDate;
    }
    return false;
  };

  return (
    <AdminRoute>
      <CRow className="bg-white p-3 rounded-md mb-3 mx-auto align-items-center">
        <CCol className="col-12 col-sm-7 col-md-8">
          <p className="organize__offer__description">
            Déplacer une offre ou entrer un numéro pour spécifier son
            emplacement dans l'application
          </p>
        </CCol>
        <CCol className="col-12 col-sm-5 col-md-4 mt-3 mt-sm-0">
          <div className="d-flex align-items-center justify-content-between">
            <SearchForm onChange={setSearchQuery} value={searchQuery} />
            <button
              onClick={savePositions}
              className="btn btn-danger text-white ms-auto d-block shadow-primary"
              disabled={!arrangedOffers?.length || saveOrderMutation.isLoading}
            >
              Mettre à jour
            </button>
          </div>
        </CCol>
      </CRow>

      {isLoading ? (
        <div className="d-flex justify-content-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      ) : arrangedOffers.length === 0 ? (
        <div className="d-flex flex-column align-items-center justify-content-center my-5 py-5 bg-white rounded-md">
          <MagnifyingGlassIcon width={48} height={48} color="gray" />
          <p className="text-muted mt-3 mb-1 fs-5 fw-semibold">
            {debouncedSearch
              ? "Aucune offre trouvée"
              : "Aucune offre disponible"}
          </p>
          <p className="text-muted small mb-0">
            {debouncedSearch
              ? `Aucun résultat pour "${debouncedSearch}". Essayez un autre terme.`
              : "Commencez par créer des offres pour les organiser ici."}
          </p>
        </div>
      ) : (
        <ReactSortable
          list={arrangedOffers}
          setList={setArrangedOffers}
          className="organize__offers_wrapper"
        >
          {arrangedOffers.map((offer, index) => (
            <div
              key={offer.id}
              className="organize-offer-item mb-3 p-3 bg-white rounded"
            >
              <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-2">
                <p className="text-danger my-0 text-uppercase">
                  {offersDisplayType[offer.type]}
                </p>

                {isOfferUnavailable(offer) && (
                  <div className="bg-danger text-white px-2 rounded-sm ms-2">
                    indisponible
                  </div>
                )}

                {editingOrderId === offer.id ? (
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    min="1"
                    max={arrangedOffers.length}
                    value={orderInputValue || ""}
                    onChange={(e) =>
                      setOrderInputValue(
                        e.target.value === "" ? 0 : parseInt(e.target.value),
                      )
                    }
                    style={{ width: "60px" }}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleOrderChange(
                          offer.id,
                          Math.max(1, orderInputValue),
                        );
                      } else if (e.key === "Escape") {
                        setEditingOrderId(null);
                      }
                    }}
                    onBlur={() => {
                      handleOrderChange(
                        offer.id,
                        orderInputValue < 1 ? 1 : orderInputValue,
                      );
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <p
                    className="fw-bold text-danger p-1 border text-center my-0 cursor-pointer"
                    style={{
                      marginLeft: "auto",
                      width: "30px",
                      height: "30px",
                    }}
                    onClick={() => {
                      setOrderInputValue(index + 1);
                      setEditingOrderId(offer.id);
                    }}
                    title="Cliquez pour modifier l'ordre"
                  >
                    {index + 1}
                  </p>
                )}
              </div>

              <div className="d-flex align-items-center gap-2">
                <img
                  src={
                    offer.image || offer.event?.image || offerPlaceholderImage
                  }
                  alt={offer.title}
                  width="45px"
                  height="45px"
                  style={{ objectFit: "cover" }}
                />
                <p className="my-0 fw-semibold">
                  {offer.title || offer.event?.name}
                </p>
              </div>
            </div>
          ))}
        </ReactSortable>
      )}
    </AdminRoute>
  );
}
